import { Head, Link } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { AlertCircle, DollarSign, Award, Shield, TrendingUp } from 'lucide-react'
import DateRangeFilter, { DateRange, getDateLabel } from '@/components/DateRangeFilter'
import SurancebayNotice from '@/components/SurancebayNotice'
import FfrRegistrationNotice from '@/components/FfrRegistrationNotice'

interface Props extends PageProps {
  stats: {
    total_agents: number
    pending_onboarding: number
    active_licenses: number
    expiring_licenses: number
    // Absent when the organization hides commissions and equity from this
    // viewer — the matching cards are then not rendered at all.
    total_commissions?: number
    total_overrides?: number
    total_equity_credits?: number
  }
  has_licenses: boolean
  date_range: DateRange
  show_surancebay_notice: boolean
  show_ffr_registration_notice: boolean
}

function formatCurrency(val: number) {
  return `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function formatCredits(val: number) {
  return val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

export default function DashboardIndex({ auth, stats, has_licenses, date_range, show_surancebay_notice, show_ffr_registration_notice }: Props) {
  const isAdmin = auth.user && ['administrator', 'owner', 'staff'].includes(auth.user.role)

  const periodDescription = date_range.preset === 'all_time' ? 'all time' : getDateLabel(date_range)

  return (
    <AppLayout>
      <Head title="Dashboard" />
      
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">
              {isAdmin ? 'Admin Dashboard' : 'Welcome Back'}
            </h1>
            <p className="text-chestnut-charcoal/70 mt-1">
              {auth.user?.first_name ? `Hello, ${auth.user.first_name}` : 'Welcome to Chestnut Capital'}
            </p>
          </div>

          {!isAdmin && (
            <DateRangeFilter dateRange={date_range} path="/dashboard" />
          )}
        </div>

        {!isAdmin && (
          <FfrRegistrationNotice show={show_ffr_registration_notice} />
        )}

        {!isAdmin && (
          <SurancebayNotice show={show_surancebay_notice} />
        )}

        {!isAdmin && !has_licenses && (
          <Link
            href="/my/licenses"
            className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 transition-colors"
          >
            <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
            <p className="text-sm font-medium text-yellow-800">
              Remember to add your licenses to help track their expiration dates - click here
            </p>
          </Link>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {isAdmin ? (
            <>
              <StatCard
                title="Total Agents"
                value={stats.total_agents}
                description="Active agents in the network"
                color="forest"
                href="/agents"
                icon={<span className="text-2xl">👥</span>}
              />
              <StatCard
                title="Pending Onboarding"
                value={stats.pending_onboarding}
                description="Agents awaiting completion"
                color="gold"
                href="/onboarding"
                icon={<span className="text-2xl">📋</span>}
              />
              <StatCard
                title="Expiring Licenses"
                value={stats.expiring_licenses}
                description="Licenses expiring in 30 days"
                color="burgundy"
                href="/licenses?filter=expiring"
                icon={<span className="text-2xl">⚠️</span>}
              />
            </>
          ) : (
            <>
              {stats.total_commissions !== undefined && (
                <StatCard
                  title="My Commissions"
                  value={formatCurrency(stats.total_commissions)}
                  description={`Total earnings — ${periodDescription}`}
                  color="forest"
                  href="/my/commissions"
                  icon={<DollarSign className="h-6 w-6 text-chestnut-forest" />}
                />
              )}
              {stats.total_overrides !== undefined && stats.total_overrides > 0 && (
                <StatCard
                  title="Override Earnings"
                  value={formatCurrency(stats.total_overrides)}
                  description={`From sponsored agents — ${periodDescription}`}
                  color="gold"
                  href="/my/commissions"
                  icon={<TrendingUp className="h-6 w-6 text-chestnut-gold" />}
                />
              )}
              {stats.total_equity_credits !== undefined && (
                <StatCard
                  title="Equity Credits"
                  value={formatCredits(stats.total_equity_credits)}
                  description={`Total credits earned — ${periodDescription}`}
                  color="gold"
                  href="/my/equity"
                  icon={<Award className="h-6 w-6 text-chestnut-gold" />}
                />
              )}
              <StatCard
                title="Active Licenses"
                value={stats.active_licenses}
                description="Across all states"
                color="forest"
                href="/my/licenses"
                icon={<Shield className="h-6 w-6 text-chestnut-forest" />}
              />
            </>
          )}
        </div>

        <div className="card">
          <h2 className="text-xl font-semibold text-chestnut-charcoal font-heading mb-4">
            Recent Activity
          </h2>
          <p className="text-chestnut-charcoal/60">
            Activity feed will appear here once you start using the platform.
          </p>
        </div>
      </div>
    </AppLayout>
  )
}

function StatCard({ 
  title, 
  value, 
  description, 
  color,
  href,
  icon
}: { 
  title: string
  value: string | number
  description: string
  color: 'forest' | 'gold' | 'burgundy'
  href: string
  icon?: React.ReactNode
}) {
  const colorClasses = {
    forest: 'border-l-chestnut-forest',
    gold: 'border-l-chestnut-gold',
    burgundy: 'border-l-chestnut-burgundy',
  }

  return (
    <Link 
      href={href}
      className={`card border-l-4 ${colorClasses[color]} hover:shadow-md hover:scale-[1.02] transition-all duration-200 cursor-pointer block`}
    >
      <div className="flex items-start justify-between">
        <h3 className="text-sm font-medium text-chestnut-charcoal/60 uppercase tracking-wide">
          {title}
        </h3>
        {icon && <div className="flex-shrink-0">{icon}</div>}
      </div>
      <p className="text-3xl font-bold text-chestnut-charcoal mt-2">
        {value}
      </p>
      <p className="text-sm text-chestnut-charcoal/60 mt-1">
        {description}
      </p>
    </Link>
  )
}
